import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { EMPTY_THEME_FORM, type ThemeFormValues } from '@/components/DesignSystem/ThemeEditorPanel';
import { ComponentShowcase } from '@/components/DesignSystem/ComponentShowcase';
import { ThemePresetPicker } from '@/components/DesignSystem/ThemePresetPicker';
import type { ThemePreset } from '@/components/DesignSystem/ThemePresetCard';
import { loadPresetIntoForm } from '@/components/DesignSystem/loadPresetIntoForm';
import { findMatchingPresetId } from './findMatchingPresetId';
import { useThemePreview } from '@/hooks/useThemePreview';
import { Button } from '@/components/ui/Button/Button';
import { applyTheme, type ResolvedTheme } from '@/lib/orgTheme';
import { setLocaleSettings } from '@/lib/localeFormatters';
import { useLocaleStore } from '@/stores/localeStore';

// Build-time flag that hides the developer-oriented design surface (For
// Developers notice + Foundation group + component preview groups) from the
// org-scoped Design System tab. Default: enabled. Set VITE_HIDE_ORGANIZATION_DEV_DESIGN=false
// in .env to opt back into the full editable view. The super-admin
// /admin/design-system page is unaffected — it never reads this var.
const HIDE_DEV_DESIGN = import.meta.env.VITE_HIDE_ORGANIZATION_DEV_DESIGN !== 'false';

/**
 * Organization Theme Settings — inline design system style (matches super admin).
 * Full-width component library with inline theme editing controls.
 * Sticky save bar at the top for quick save/reset.
 */
/**
 * ThemeSettingsPanel — content only, used in the System Settings "Design System"
 * tab. Mirrors the super-admin DesignSystem page (preset picker + color
 * foundation) but scopes saves to the active organization.
 */
export function ThemeSettingsPanel() {
  const orgId = useOrgStore(s => s.activeOrgId);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ThemeFormValues>(EMPTY_THEME_FORM);
  const [saved, setSaved] = useState(false);
  /**
   * AC-Design-2: Track the user's explicit preset selection separately so
   * that `activePresetId` (derived below via useMemo) is computed in the
   * same render that query data becomes available — eliminating the extra
   * render cycle / flicker that the previous useEffect approach caused.
   *
   * undefined = user hasn't interacted yet → fall back to computed match
   * null      = user explicitly deselected (custom theme, no match)
   * string    = user clicked a specific preset
   */
  const [userPresetSelection, setUserPresetSelection] = useState<string | null | undefined>(undefined);

  // Fetch read-only catalog of platform presets (org-admin accessible).
  const { data: presets = [] } = useQuery({
    queryKey: ['theme-presets-org'],
    queryFn: async () => {
      const res = await apiFetch('/api/theme-presets');
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data || []) as ThemePreset[];
    },
  });

  // Fetch platform defaults (to show as placeholders)
  const { data: platformDefaults } = useQuery({
    queryKey: ['platform-theme'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/platform-theme');
      if (!res.ok) return null;
      const json = await res.json();
      return json.data as Record<string, any> | null;
    },
  });

  const handlePickPreset = (preset: ThemePreset) => {
    setUserPresetSelection(preset.id);
    setForm(loadPresetIntoForm(preset as unknown as Record<string, any>));
  };

  /** Build a ResolvedTheme from a source record (preset / platform / org). */
  const buildResolvedFromSource = useCallback(
    (src: Record<string, any> | null | undefined): ResolvedTheme | null => {
      if (!src) return null;
      return {
        primaryColor: src.primaryColor || null,
        secondaryColor: src.secondaryColor || null,
        accentColor: src.accentColor || null,
        successColor: src.successColor || null,
        warningColor: src.warningColor || null,
        errorColor: src.errorColor || null,
        infoColor: src.infoColor || null,
        grayColor: src.grayColor || null,
        headingFontFamily: src.headingFontFamily || null,
        bodyFontFamily: src.bodyFontFamily || null,
        headingFontSize: src.headingFontSize || null,
        bodyFontSize: src.bodyFontSize || null,
        headingFontUrl: src.headingFontUrl || null,
        bodyFontUrl: src.bodyFontUrl || null,
        logoUrl: src.logoUrl || null,
        logoMarkUrl: src.logoMarkUrl || null,
        faviconUrl: src.faviconUrl || null,
        borderRadius: src.borderRadius || null,
        customCss: src.customCss || null,
        darkModeEnabled: src.darkModeEnabled ?? true,
      };
    },
    [],
  );

  // Fetch org theme
  const { data: orgTheme } = useQuery({
    queryKey: ['org-theme', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await apiFetch(`/api/orgs/${orgId}/theme`);
      if (!res.ok) return null;
      const json = await res.json();
      return json.data as Record<string, any> | null;
    },
    enabled: !!orgId,
  });

  /**
   * AC-Design-2: Derive the active preset ID synchronously — in the same
   * render that query data becomes available — so the correct card is
   * highlighted on first paint rather than one render cycle later.
   *
   * Priority:
   *   1. User's explicit selection (userPresetSelection !== undefined)
   *   2. Computed match against the saved org theme (no user interaction yet)
   *
   * Placed AFTER the orgTheme query so TypeScript sees orgTheme in scope.
   */
  const activePresetId = useMemo(() => {
    if (userPresetSelection !== undefined) return userPresetSelection;
    return findMatchingPresetId(orgTheme, presets);
  }, [userPresetSelection, orgTheme, presets]);

  // Populate form when org theme loads — but only ONCE per active org.
  //
  // WHY THE REF GUARD: the previous implementation fired this effect on
  // every orgTheme reference change, which meant any React Query refetch
  // (window refocus, save invalidate, stale-time zero default) would
  // clobber a preset the user had just clicked. This was the real cause
  // of "click a preset, card highlights but visual doesn't change" on
  // the ROROS org settings page — useOrgTheme kept the saved org theme
  // active on :root, useThemePreview tried to paint the preset, but this
  // effect kept slamming the form back to the saved org values before the
  // 200ms debounce could fire.
  //
  // After initial population, the user owns the form state — subsequent
  // orgTheme refetches must not touch it. Preset picks, manual edits,
  // save, and reset all drive the form explicitly.
  const initializedForOrgRef = useRef<string | null>(null);

  // Switching orgs re-arms the initializer so the form reloads from the new
  // org's saved theme, and clears any explicit user preset selection so the
  // computed match takes effect immediately for the new org.
  useEffect(() => {
    if (orgId && initializedForOrgRef.current !== null && initializedForOrgRef.current !== orgId) {
      initializedForOrgRef.current = null;
      setUserPresetSelection(undefined);
    }
  }, [orgId]);

  // Populate the form ONCE per active org when org theme loads.
  //
  // AC-Design-2 note: activePresetId is now derived via useMemo (above) so
  // this effect only manages form state — not the preset highlight. The form
  // still needs to be populated from the saved org theme so the color editors
  // show the right values.
  //
  // The ref guard prevents subsequent refetches (window focus, save
  // invalidation) from clobbering a preset the user already picked.
  useEffect(() => {
    if (!orgId) return;
    if (initializedForOrgRef.current === orgId) return;
    if (orgTheme === undefined) return; // still loading — wait for resolution
    if (presets.length === 0) return;   // wait for preset catalog (needed so
                                        // the form starts from the correct
                                        // saved values, not empty defaults)
    initializedForOrgRef.current = orgId;

    if (orgTheme) {
      setForm({
        primaryColor: orgTheme.primaryColor || '',
        secondaryColor: orgTheme.secondaryColor || '',
        accentColor: orgTheme.accentColor || '',
        successColor: orgTheme.successColor || '',
        warningColor: orgTheme.warningColor || '',
        errorColor: orgTheme.errorColor || '',
        infoColor: orgTheme.infoColor || '',
        grayColor: orgTheme.grayColor || '',
        headingFontFamily: orgTheme.headingFontFamily || '',
        bodyFontFamily: orgTheme.bodyFontFamily || '',
        headingFontSize: orgTheme.headingFontSize || '',
        bodyFontSize: orgTheme.bodyFontSize || '',
        logoUrl: orgTheme.logoUrl || '',
        logoMarkUrl: orgTheme.logoMarkUrl || '',
        faviconUrl: orgTheme.faviconUrl || '',
        borderRadius: orgTheme.borderRadius || '',
        customCss: orgTheme.customCss || '',
        darkModeEnabled: orgTheme.darkModeEnabled ?? true,
        dateFormat: orgTheme.dateFormat || '',
        timeFormat: orgTheme.timeFormat || '',
        weekStart: orgTheme.weekStart ?? '',
        showCalendarWeeks: orgTheme.showCalendarWeeks ?? true,
        thousandsSeparator: orgTheme.thousandsSeparator || '',
        decimalSeparator: orgTheme.decimalSeparator || '',
        currencySymbol: orgTheme.currencySymbol || '',
        currencyFormat: orgTheme.currencyFormat || '',
      });
    }
    // If orgTheme is null (no overrides saved yet), leave form as
    // EMPTY_THEME_FORM so placeholders show the platform defaults.
  }, [orgTheme, orgId, presets]);

  // Live preview.
  //
  // IMPORTANT: this object is memoised so its reference is stable across
  // renders that don't change `form`, `activePresetId`, or `platformDefaults`.
  // Without the memo, every render produced a new object, which made
  // useThemePreview's internal useDebounce re-arm its 200ms timer on every
  // render — so unrelated re-renders (React Query refetches, translation
  // dictionary updates, etc.) kept cancelling the pending preview apply.
  // That was the original "click a preset but nothing happens visually"
  // regression. Memoising the object lets the debounce timer actually fire.
  //
  // Fallback policy:
  //   • No active preset → fall back to platformDefaults so the page paints
  //     correctly even when the org hasn't customised anything yet.
  //   • Active preset     → no fallback, so the preset's values dominate
  //     exactly (a null field in the preset stays null, matching the
  //     super-admin DesignSystem behaviour).
  const previewTheme: ResolvedTheme = useMemo(() => {
    const fb: Record<string, any> = activePresetId ? {} : (platformDefaults || {});
    return {
      primaryColor: form.primaryColor || fb.primaryColor || null,
      secondaryColor: form.secondaryColor || fb.secondaryColor || null,
      accentColor: form.accentColor || fb.accentColor || null,
      successColor: form.successColor || fb.successColor || null,
      warningColor: form.warningColor || fb.warningColor || null,
      errorColor: form.errorColor || fb.errorColor || null,
      infoColor: form.infoColor || fb.infoColor || null,
      grayColor: form.grayColor || fb.grayColor || null,
      headingFontFamily: form.headingFontFamily || fb.headingFontFamily || null,
      bodyFontFamily: form.bodyFontFamily || fb.bodyFontFamily || null,
      headingFontSize: form.headingFontSize || fb.headingFontSize || null,
      bodyFontSize: form.bodyFontSize || fb.bodyFontSize || null,
      headingFontUrl: null,
      bodyFontUrl: null,
      logoUrl: form.logoUrl || fb.logoUrl || null,
      logoMarkUrl: form.logoMarkUrl || fb.logoMarkUrl || null,
      faviconUrl: form.faviconUrl || fb.faviconUrl || null,
      borderRadius: form.borderRadius || fb.borderRadius || null,
      customCss: form.customCss || fb.customCss || null,
      darkModeEnabled: form.darkModeEnabled,
    };
  }, [form, activePresetId, platformDefaults]);
  useThemePreview(previewTheme);

  // Save mutation — sends only org-level overrides
  const saveMutation = useMutation({
    mutationFn: async (data: ThemeFormValues) => {
      const body: Record<string, any> = {};
      body.primaryColor = data.primaryColor || null;
      body.secondaryColor = data.secondaryColor || null;
      body.accentColor = data.accentColor || null;
      body.successColor = data.successColor || null;
      body.warningColor = data.warningColor || null;
      body.errorColor = data.errorColor || null;
      body.infoColor = data.infoColor || null;
      body.grayColor = data.grayColor || null;
      body.headingFontFamily = data.headingFontFamily || null;
      body.bodyFontFamily = data.bodyFontFamily || null;
      body.headingFontSize = data.headingFontSize || null;
      body.bodyFontSize = data.bodyFontSize || null;
      body.logoUrl = data.logoUrl || null;
      body.logoMarkUrl = data.logoMarkUrl || null;
      body.faviconUrl = data.faviconUrl || null;
      body.borderRadius = data.borderRadius || null;
      body.customCss = data.customCss || null;
      body.darkModeEnabled = data.darkModeEnabled;
      body.dateFormat = data.dateFormat || null;
      body.timeFormat = data.timeFormat || null;
      body.weekStart = data.weekStart === '' ? null : data.weekStart;
      body.showCalendarWeeks = data.showCalendarWeeks;
      body.thousandsSeparator = data.thousandsSeparator || null;
      body.decimalSeparator = data.decimalSeparator || null;
      body.currencySymbol = data.currencySymbol || null;
      body.currencyFormat = data.currencyFormat || null;

      const res = await apiFetch(`/api/orgs/${orgId}/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Failed to save theme');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-theme', orgId] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  // Reset mutation — clears org overrides AND snaps the visual + form back
  // to platform defaults.
  //
  // The regression this fixes: previously onSuccess only called
  // setForm(EMPTY_THEME_FORM), which emptied the form fields but left
  // activePresetId set and left the DOM styled by the previously-picked
  // preset. We now:
  //   1. Clear activePresetId so the preset grid de-selects.
  //   2. Populate form from platformDefaults so the editor shows the values
  //      the user will actually see (not blank placeholders).
  //   3. Call applyTheme(platformDefaults) synchronously so the visual
  //      snaps immediately — without waiting for the preview debounce or
  //      for useOrgTheme to refetch.
  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/orgs/${orgId}/theme`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to reset');
    },
    onSuccess: () => {
      if (platformDefaults) {
        setForm(loadPresetIntoForm(platformDefaults));
        // After reset, clear the explicit user selection so the computed
        // match (derived from the org-theme query, which we invalidate
        // below) takes over automatically once the refetch resolves.
        // Also re-arm the form initializer so it repopulates from the
        // freshly-fetched org theme.
        setUserPresetSelection(undefined);
        initializedForOrgRef.current = null;
        const resolved = buildResolvedFromSource(platformDefaults);
        if (resolved) applyTheme(resolved);
      } else {
        setForm(EMPTY_THEME_FORM);
        setUserPresetSelection(null);
        applyTheme(null);
      }
      queryClient.invalidateQueries({ queryKey: ['org-theme', orgId] });
    },
  });

  const updateLocaleStore = useLocaleStore(s => s.updateSettings);

  // Locale-related keys that need to sync to the reactive locale store
  const LOCALE_KEYS = new Set(['dateFormat', 'timeFormat', 'thousandsSeparator', 'decimalSeparator', 'currencySymbol', 'currencyFormat']);

  const updateField = useCallback(<K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));

    // Sync locale-related fields to the reactive locale store so date/time components update live
    if (LOCALE_KEYS.has(key) && typeof value === 'string' && value !== '') {
      const partial = { [key]: value };
      setLocaleSettings(partial);
      updateLocaleStore(partial);
    }
  }, [updateLocaleStore]);

  if (!orgId) return null;

  // Convert platform defaults to form format for placeholders
  const platformFormDefaults: Partial<ThemeFormValues> | null = platformDefaults ? {
    primaryColor: platformDefaults.primaryColor || '',
    secondaryColor: platformDefaults.secondaryColor || '',
    accentColor: platformDefaults.accentColor || '',
    successColor: platformDefaults.successColor || '',
    warningColor: platformDefaults.warningColor || '',
    errorColor: platformDefaults.errorColor || '',
    infoColor: platformDefaults.infoColor || '',
    grayColor: platformDefaults.grayColor || '',
    headingFontFamily: platformDefaults.headingFontFamily || '',
    bodyFontFamily: platformDefaults.bodyFontFamily || '',
  } : null;

  return (
    <div data-no-translate>
      {/* Sticky save bar */}
      <div className="sticky top-16 z-20 bg-background/95 backdrop-blur border-b border-border px-6 py-3">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Organization Design</h2>
            {/* AC-Design-1: merged subtitle — replaces "Override platform defaults…" */}
            <p className="text-xs text-muted-foreground">
              Select your preferred design for Rocket Routine OS.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-success-600">Saved ✓</span>
            )}
            {saveMutation.error && (
              <span className="text-sm text-error-600">{(saveMutation.error as Error).message}</span>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => resetMutation.mutate()} loading={resetMutation.isPending}>
              Reset to Platform
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={() => saveMutation.mutate(form)} loading={saveMutation.isPending}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Theme Presets — read-only catalog of platform-curated presets.
          AC-Design-1: hideHeader=true removes the "THEME PRESETS" sub-heading
          so only the single merged header in the sticky bar is visible. */}
      {presets.length > 0 && (
        <ThemePresetPicker
          presets={presets}
          activePresetId={activePresetId}
          onPick={handlePickPreset}
          readOnly
          hideHeader
        />
      )}

      {/* Full-width Component Showcase with inline editors —
          gated by VITE_HIDE_ORGANIZATION_DEV_DESIGN. When the var is unset
          or anything other than the literal string "false", the entire
          showcase (For Developers notice + Foundation + preview groups)
          is unmounted. Form state lives above in this component, so the
          preset picker → Save flow keeps working without it. */}
      {!HIDE_DEV_DESIGN && (
        <ComponentShowcase
          hideNav
          form={form}
          onChange={updateField}
          platformDefaults={platformFormDefaults}
        />
      )}
    </div>
  );
}

/** Legacy page wrapper — the /organization/theme route still uses this. */
export function ThemeSettings() {
  return <ThemeSettingsPanel />;
}
